import React, { createContext, useContext, useState, ReactNode } from "react";
import { recipesApi } from "../api/recipes";
import {
  RecipeApiResponse,
  FetchedRecipe,
  Author,
  Contributor,
} from "../types/recipe";

/**
 * RecipeContextType defines the shape of the context state and setters
 * used for managing recipe-related data throughout the application.
 */
interface RecipeContextType {
  recipe: FetchedRecipe | null; // Currently selected recipe
  author: Author | null; // Author of the recipe
  fullContributors: Contributor[]; // List of all contributors
  linkedRecipes: any[]; // Recipes linked to the current recipe
  loading: boolean; // Loading state for async operations
  error: string | null; // Error message, if any
  setRecipe: React.Dispatch<React.SetStateAction<FetchedRecipe | null>>; // Setter for recipe
  setAuthor: React.Dispatch<React.SetStateAction<Author | null>>; // Setter for author
  setFullContributors: React.Dispatch<React.SetStateAction<Contributor[]>>; // Setter for contributors
  setLinkedRecipes: React.Dispatch<React.SetStateAction<any[]>>; // Setter for linked recipes
  setLoading: React.Dispatch<React.SetStateAction<boolean>>; // Setter for loading state
  setError: React.Dispatch<React.SetStateAction<string | null>>; // Setter for error state
}

// Create the RecipeContext with an undefined default value
const RecipeContext = createContext<RecipeContextType | undefined>(undefined);

/**
 * RecipeProvider wraps children components and provides recipe-related state
 * and setters via React Context. Use this at a high level in your component tree.
 */
export const RecipeProvider = ({ children }: { children: ReactNode }) => {
  // State hooks for recipe context values
  const [recipe, setRecipe] = useState<FetchedRecipe | null>(null);
  const [author, setAuthor] = useState<Author | null>(null);
  const [fullContributors, setFullContributors] = useState<Contributor[]>([]);
  const [linkedRecipes, setLinkedRecipes] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Provide context values and setters to children
  return (
    <RecipeContext.Provider
      value={{
        recipe,
        author,
        fullContributors,
        linkedRecipes,
        loading,
        error,
        setRecipe,
        setAuthor,
        setFullContributors,
        setLinkedRecipes,
        setLoading,
        setError,
      }}
    >
      {children}
    </RecipeContext.Provider>
  );
};

/**
 * useRecipeContext is a custom hook to access the RecipeContext.
 * Throws an error if used outside of a RecipeProvider.
 */
export const useRecipeContext = () => {
  const context = useContext(RecipeContext);
  if (!context) {
    throw new Error("useRecipeContext must be used within a RecipeProvider");
  }
  return context;
};
